import { applyMiddleware, combineReducers, createStore } from 'redux';
import { reducer as form } from 'redux-form';
import SagaMiddleware from 'redux-saga';

import { getTaxRateIdLocalStorageKey } from '@/helpers/localStorageKeyHelper';

import accountReducer from '@payaca/store/account/accountReducer';
import accountSagaCreator from '@payaca/store/account/accountSaga';
import analyticsReducer from '@payaca/store/analytics/analyticsReducer';
import analyticsSagaCreator from '@payaca/store/analytics/analyticsSaga';
import authReducer from '@payaca/store/auth/authReducer';
import authSagaCreator from '@payaca/store/auth/authSaga';
import automationsReducer from '@payaca/store/automations/automationReducer';
import automationsSagaCreator from '@payaca/store/automations/automationSaga';
import clientReducer from '@payaca/store/client/clientReducer';
import clientSagaCreator from '@payaca/store/client/clientSaga';
import countriesReducer from '@payaca/store/countries/reducer';
import countriesSagaCreator from '@payaca/store/countries/saga';
import csvUploadReducer from '@payaca/store/csvUpload/csvUploadReducer';
import csvUploadSagaCreator from '@payaca/store/csvUpload/csvUploadSaga';
import customerReducer from '@payaca/store/customer/customerReducer';
import customerSagaCreator from '@payaca/store/customer/customerSaga';
import dashboardReducer from '@payaca/store/dashboard/dashboardReducer';
import dashboardSagaCreator from '@payaca/store/dashboard/dashboardSaga';
import dealsReducer from '@payaca/store/deals/dealsReducer';
import dealsSagaCreator from '@payaca/store/deals/dealsSaga';
import dealTemplateReducer from '@payaca/store/dealTemplates/dealTemplateReducer';
import dealTemplateSagaCreator from '@payaca/store/dealTemplates/dealTemplateSaga';
import dispatchReducer from '@payaca/store/dispatch/dispatchReducer';
import dispatchSagaCreator from '@payaca/store/dispatch/dispatchSaga';
import documentsReducer from '@payaca/store/documents/documentReducer';
import documentsSagaCreator from '@payaca/store/documents/documentSaga';
import fileDownloadReducer from '@payaca/store/fileDownload/fileDownloadReducer';
import fileDownloadSagaCreator from '@payaca/store/fileDownload/fileDownloadSaga';
import formsReducer from '@payaca/store/forms/formsReducer';
import formsSagaCreator from '@payaca/store/forms/formsSaga';
import invoicesReducer from '@payaca/store/invoices/invoicesReducer';
import invoicesSagaCreator from '@payaca/store/invoices/invoicesSaga';
import jobContentReducer from '@payaca/store/jobContent/jobContentReducer';
import jobContentSagaCreator from '@payaca/store/jobContent/jobContentSaga';
import jobPaymentsReducer from '@payaca/store/jobPayments/jobPaymentsReducer';
import jobPaymentsSagaCreator from '@payaca/store/jobPayments/jobPaymentsSaga';
import jobsStoreReducer from '@payaca/store/jobs/jobsReducer';
import jobsStoreSagaCreator from '@payaca/store/jobs/jobsSaga';
import lineItemGroupsReducer from '@payaca/store/lineItemGroups/lineItemGroupsReducer';
import lineItemGroupsSagaCreator from '@payaca/store/lineItemGroups/lineItemGroupsSaga';
import lineItemsReducer from '@payaca/store/lineItems/lineItemsReducer';
import lineItemsSagaCreator from '@payaca/store/lineItems/lineItemsSaga';
import lineItemsV2Reducer from '@payaca/store/lineItemsV2/lineItemsReducer';
import lineItemsV2SagaCreator from '@payaca/store/lineItemsV2/lineItemsSaga';
import listedItemsReducer from '@payaca/store/listedItems/listedItemsReducer';
import listedItemsSagaCreator from '@payaca/store/listedItems/listedItemsSaga';
import materialReducer from '@payaca/store/materials/materialsReducer';
import materialSagaCreator from '@payaca/store/materials/materialsSaga';
import materialsListReducer from '@payaca/store/materialsList/materialsListReducer';
import materialsListSagaCreator from '@payaca/store/materialsList/materialsListSaga';
import notesReducer from '@payaca/store/notes/notesReducer';
import notesSagaCreator from '@payaca/store/notes/notesSaga';
import pdfDocumentDataSagaCreator from '@payaca/store/pdfDocumentData/pdfDocumentDataSaga';
import pipelinesReducer from '@payaca/store/pipelines/pipelineReducer';
import pipelinesSagaCreator from '@payaca/store/pipelines/pipelineSaga';
import proposalsReducer from '@payaca/store/proposals/proposalsReducer';
import proposalsSagaCreator from '@payaca/store/proposals/proposalsSaga';
import scheduledEventsReducer from '@payaca/store/scheduledEvents/scheduledEventsReducer';
import scheduledEventsSagaCreator from '@payaca/store/scheduledEvents/scheduledEventsSaga';
import servicePlansReducer from '@payaca/store/servicePlans/servicePlansReducer';
import servicePlansSagaCreator from '@payaca/store/servicePlans/servicePlansSaga';
import subscriptionReducer from '@payaca/store/subscription/subscriptionReducer';
import subscriptionSagaCreator from '@payaca/store/subscription/subscriptionSaga';
import supplierReducer from '@payaca/store/suppliers/suppliersReducer';
import supplierSagaCreator from '@payaca/store/suppliers/suppliersSaga';
import tagsReducer from '@payaca/store/tags/tagsReducer';
import tagsSagaCreator from '@payaca/store/tags/tagsSaga';
import tasksReducer from '@payaca/store/tasks/tasksReducer';
import tasksSagaCreator from '@payaca/store/tasks/tasksSaga';
import taxRatesReducer from '@payaca/store/tax-rates/reducer';
import taxRatesSagaCreator from '@payaca/store/tax-rates/saga';
import templatesSagaCreator from '@payaca/store/templates/templatesSaga';
import timelogsReducer from '@payaca/store/timelogs/timelogsReducer';
import timelogsSagaCreator from '@payaca/store/timelogs/timelogsSaga';
import uploadsReducer from '@payaca/store/uploads/uploadsReducer';
import uploadsSagaCreator from '@payaca/store/uploads/uploadsSaga';
import { uploadFileToStorage } from '@/api/mutations/file/uploadFileToStorage';
import userReducer from '@payaca/store/user/userReducer';
import userSagaCreator from '@payaca/store/user/userSaga';
import validationReducer from '@payaca/store/validation/validationReducer';
import validationSagaCreator from '@payaca/store/validation/validationSaga';
import {
  reducer as accountSettings,
  saga as accountSettingsSaga,
} from './accountSettings';
import { reducer as app, sagaCreator as appSaga } from './app';
import { saga as jobsSaga } from './jobs';
import { reducer as users, saga as usersSaga } from './users';

export const TOKEN_KEY = 'payacav1';
export const REFRESH_TOKEN_KEY = 'payacav1-refreshToken';

const sagaMiddleware = new SagaMiddleware();

const reducer = combineReducers({
  app,
  users,
  accountSettings,
  form,
  analytics: analyticsReducer,
  account: accountReducer,
  auth: authReducer,
  listedItems: listedItemsReducer,
  jobPayments: jobPaymentsReducer,
  jobsStore: jobsStoreReducer,
  lineItems: lineItemsReducer,
  subscription: subscriptionReducer,
  customer: customerReducer,
  user: userReducer,
  deals: dealsReducer,
  lineItemGroups: lineItemGroupsReducer,
  lineItemsV2: lineItemsV2Reducer,
  fileDownload: fileDownloadReducer,
  scheduledEvents: scheduledEventsReducer,
  dashboard: dashboardReducer,
  jobContent: jobContentReducer,
  documents: documentsReducer,
  forms: formsReducer,
  notes: notesReducer,
  tags: tagsReducer,
  invoices: invoicesReducer,
  tasks: tasksReducer,
  pipelines: pipelinesReducer,
  automations: automationsReducer,
  suppliers: supplierReducer,
  uploads: uploadsReducer,
  materials: materialReducer,
  csvUpload: csvUploadReducer,
  materialsLists: materialsListReducer,
  dealTemplates: dealTemplateReducer,
  validation: validationReducer,
  proposals: proposalsReducer,
  client: clientReducer,
  taxRates: taxRatesReducer,
  countries: countriesReducer,
  servicePlans: servicePlansReducer,
  dispatch: dispatchReducer,
  timelogs: timelogsReducer,
});

const logger = () => {
  return (next) => (action) => {
    if (import.meta.env.VITE_APP_ENV === 'local') {
      // console.log('[ACTION]:', action);
    }
    // Call the next dispatch method in the middleware chain.
    const returnValue = next(action);
    // Likely the action itself, unless a middleware further in chain changed it.
    return returnValue;
  };
};

export const store = createStore(
  reducer,
  applyMiddleware(sagaMiddleware, logger)
);

const basicSagaOptions = {
  apiBaseurl: import.meta.env.VITE_API_HOST,
  getAuthHeader: () => `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
};

const sagas = [
  ...[
    analyticsSagaCreator,
    listedItemsSagaCreator,
    jobPaymentsSagaCreator,
    customerSagaCreator,
    userSagaCreator,
    dealsSagaCreator,
    dashboardSagaCreator,
    documentsSagaCreator,
    formsSagaCreator,
    notesSagaCreator,
    tagsSagaCreator,
    tasksSagaCreator,
    pipelinesSagaCreator,
    automationsSagaCreator,
    supplierSagaCreator,
    materialSagaCreator,
    csvUploadSagaCreator,
    materialsListSagaCreator,
    dealTemplateSagaCreator,
    validationSagaCreator,
    proposalsSagaCreator,
    taxRatesSagaCreator,
    clientSagaCreator,
    pdfDocumentDataSagaCreator,
    countriesSagaCreator,
    templatesSagaCreator,
    timelogsSagaCreator,
  ].map((sagaCreator) => sagaCreator(basicSagaOptions)),

  ...[
    subscriptionSagaCreator,
    accountSagaCreator,
    scheduledEventsSagaCreator,
    fileDownloadSagaCreator,
    invoicesSagaCreator,
    lineItemGroupsSagaCreator,
    lineItemsSagaCreator,
    servicePlansSagaCreator,
    dispatchSagaCreator,
  ].map((sagaCreator) =>
    sagaCreator({
      ...basicSagaOptions,
      isNativeApp: false,
    })
  ),

  uploadsSagaCreator({
    ...basicSagaOptions,
    // Called through a wrapper rather than passed by reference, because this
    // module and the upload module's `graphql-client` import each other, so
    // whichever evaluates second is still uninitialised at this point.
    uploadFile: (file, fileName, linkedEntities) =>
      uploadFileToStorage(file, fileName, linkedEntities),
  }),

  authSagaCreator({
    ...basicSagaOptions,
    getRefreshToken: async () => {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    },
    storeAuthTokens: async (authTokens) => {
      localStorage.setItem(TOKEN_KEY, authTokens.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, authTokens.refreshToken);
    },
  }),

  jobsStoreSagaCreator({
    ...basicSagaOptions,
    isNativeApp: false,
    storage: {
      setItem: async (key, value) => localStorage.setItem(key, value),
      getItem: async (key) => localStorage.getItem(key),
    },
  }),

  jobContentSagaCreator({
    ...basicSagaOptions,
    storage: {
      setItem: async (key, value) => localStorage.setItem(key, value),
      getItem: async (key) => localStorage.getItem(key),
    },
    storageKeys: {
      taxRateId: getTaxRateIdLocalStorageKey(),
    },
  }),
  lineItemsV2SagaCreator({
    ...basicSagaOptions,
    storage: {
      setItem: async (key, value) => localStorage.setItem(key, value),
      getItem: async (key) => localStorage.getItem(key),
    },
    storageKeys: {
      taxRateId: getTaxRateIdLocalStorageKey(),
    },
  }),

  appSaga,
  usersSaga,
  jobsSaga,
  accountSettingsSaga,
];

sagas.forEach((saga) => sagaMiddleware.run(saga));
